import {
  TRANSFER_RECEPT_NEED_PRESCRIPTION_CHOICE,
  TRANSFER_RECEPT_PLAN_CHOICE_LABEL,
  TRANSFER_RECEPT_PRESCRIPTION_NEED_LABEL,
  TRANSFER_RECEPT_RETURN_CHOICE_LABEL,
  type PlannedMedicationVersion,
  type TransferPlannedMedication,
  type TransferApproval,
  type TransferReceptPlanChoice,
  type TransferReceptPrescriptionNeed,
  type TransferReview,
} from "@care-by-ella/contracts";

// TES-1241 (TES-1240) — the transfer review's Recept section, as state. The
// section starts from what is already known, so the doctor verifies instead
// of typing: the current planned medication when one exists (a care meeting
// on this case already planned it), otherwise her F-07 answers. Whatever he
// confirms or changes here is what the approval saves as the planned
// medication; the decision dialog never asks for it again.

export interface ReceptOrder {
  medication: string;
  dose: string;
  frequency: string;
  duration: string;
}

export interface TransferReceptValue {
  planChoice: TransferReceptPlanChoice | null;
  order: ReceptOrder;
  strength: string;
  plan: string;
  changeReason: string;
  need: TransferReceptPrescriptionNeed | null;
}

export type ReportedTreatment = NonNullable<
  TransferReview["reportedTreatment"]
>;
export type CurrentPlan = Pick<
  PlannedMedicationVersion,
  "medication" | "strength" | "dose" | "frequency" | "duration" | "plan"
>;

interface ReceptStart {
  order: ReceptOrder;
  strength: string;
  plan: string;
}

/** What "Bekräfta som planerad behandling" confirms. */
export function receptStart(
  reported: ReportedTreatment | null | undefined,
  current: CurrentPlan | null | undefined,
): ReceptStart {
  const from = current ?? reported;
  return {
    order: {
      medication: from?.medication ?? "",
      dose: from?.dose ?? "",
      frequency: from?.frequency ?? "",
      // F-07 asks for no length; a plan may carry one.
      duration: current?.duration ?? "",
    },
    strength: from?.strength ?? "",
    plan: current?.plan ?? "",
  };
}

export function initialRecept(
  reported: ReportedTreatment | null | undefined,
  current: CurrentPlan | null | undefined,
): TransferReceptValue {
  return {
    planChoice: null,
    ...receptStart(reported, current),
    changeReason: "",
    need: null,
  };
}

/**
 * The plan choice. "Bekräfta" puts the starting values back (what is
 * confirmed is what was reported or planned, not an edit made since);
 * "Ändra" keeps what is in the fields for the doctor to change.
 */
export function withPlanChoice(
  value: TransferReceptValue,
  choice: TransferReceptPlanChoice,
  reported: ReportedTreatment | null | undefined,
  current: CurrentPlan | null | undefined,
): TransferReceptValue {
  if (choice === "confirmed") {
    return {
      ...value,
      ...receptStart(reported, current),
      planChoice: choice,
      changeReason: "",
    };
  }
  return { ...value, planChoice: choice };
}

/**
 * TES-1242 — the case is back after kompletteringar (or a new blood test)
 * with a plan already saved: the plan choice reads "Behandlingen gäller
 * fortfarande" / "Ändra behandlingen".
 */
export function receptReturning(
  review: Pick<TransferReview, "completionRequestedAt" | "labsRequestedAt">,
  current: CurrentPlan | null | undefined,
): boolean {
  return Boolean(
    current && (review.completionRequestedAt || review.labsRequestedAt),
  );
}

export function receptPlanChoiceLabels(
  returning: boolean,
): Record<TransferReceptPlanChoice, string> {
  return returning
    ? TRANSFER_RECEPT_RETURN_CHOICE_LABEL
    : TRANSFER_RECEPT_PLAN_CHOICE_LABEL;
}

/**
 * What is still open, in the doctor's words. A change always needs its
 * medical reason, as the care meeting's does (the API refuses it
 * otherwise). `needPrescription`: the approval also needs the prescription
 * status; a komplettering never prescribes.
 */
export function receptChoiceProblems(
  value: TransferReceptValue,
  opts: {
    returning?: boolean;
    needPrescription?: boolean;
  } = {},
): {
  plannedTreatment?: string;
  changeReason?: string;
  prescriptionNeed?: string;
} {
  const labels = receptPlanChoiceLabels(opts.returning ?? false);
  return {
    ...(value.planChoice
      ? {}
      : {
          plannedTreatment: `Välj ”${labels.confirmed}” eller ”${labels.changed}”.`,
        }),
    ...(value.planChoice === "changed" && !value.changeReason.trim()
      ? {
          changeReason:
            "Ange den medicinska motiveringen till att behandlingen ändras.",
        }
      : {}),
    ...(opts.needPrescription === false || value.need
      ? {}
      : {
          prescriptionNeed: `Välj ”${TRANSFER_RECEPT_PRESCRIPTION_NEED_LABEL.existing_valid}” eller ”${TRANSFER_RECEPT_PRESCRIPTION_NEED_LABEL.new_e_prescription}”.`,
        }),
  };
}

/**
 * TES-1242 — the plan sent with "Begär komplettering" or "Nytt blodprov
 * krävs", or null while no plan choice is made (the komplettering is then
 * sent without one, as before).
 */
export function receptPlannedMedication(
  value: TransferReceptValue,
): TransferPlannedMedication | null {
  if (!value.planChoice) return null;
  const reason = value.changeReason.trim();
  return {
    medication: value.order.medication.trim(),
    dose: value.order.dose.trim(),
    frequency: value.order.frequency.trim(),
    duration: value.order.duration.trim(),
    strength: value.strength.trim(),
    plan: value.plan.trim(),
    plannedTreatment: value.planChoice,
    ...(value.planChoice === "changed" && reason
      ? { planChangeReason: reason }
      : {}),
  };
}

/** The planned medication's parts the approval carries beside the order. */
export function receptApprovalParts(
  value: TransferReceptValue,
): Pick<
  TransferApproval,
  "strength" | "plan" | "plannedTreatment" | "planChangeReason"
> {
  const reason = value.changeReason.trim();
  return {
    strength: value.strength.trim(),
    plan: value.plan.trim(),
    ...(value.planChoice ? { plannedTreatment: value.planChoice } : {}),
    ...(value.planChoice === "changed" && reason
      ? { planChangeReason: reason }
      : {}),
  };
}

/**
 * The approval's prescription status, as far as the Recept section decided
 * it: an existing prescription is preselected with the date she reported;
 * a new e-prescription is left for the doctor to confirm as issued in
 * Webdoc. `choices` is the one choice the Webdoc step offers.
 */
export function receptPrescriptionPreset(
  need: TransferReceptPrescriptionNeed,
  reported: ReportedTreatment | null | undefined,
): {
  choice: "existing_valid" | null;
  existingValidUntil: string;
  choices: readonly ("existing_valid" | "new_issued")[];
} {
  const choice = TRANSFER_RECEPT_NEED_PRESCRIPTION_CHOICE[need];
  return choice === "existing_valid"
    ? {
        choice,
        existingValidUntil: reported?.prescriptionValidUntilDate ?? "",
        choices: [choice],
      }
    : { choice: null, existingValidUntil: "", choices: [choice] };
}
